import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Send, Link2, Unplug, Mail } from "lucide-react";
import { API } from "@/config";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function TelegramPanel({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");

  const load = () =>
    axios
      .get(`${API}/admin/telegram`, { headers })
      .then((res) => setCfg(res.data))
      .catch(() => toast.error("No se pudo cargar la configuración de Telegram"));

  useEffect(() => {
    load();
  }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res?.data && key !== "test") setCfg(res.data);
      toast.success(okMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Algo salió mal");
    } finally {
      setBusy("");
    }
  };

  const saveToken = () =>
    run("token", () => axios.put(`${API}/admin/telegram`, { token }, { headers }), "Bot guardado. Ahora vincula tu chat").then(() => setToken(""));
  const link = () => run("link", () => axios.post(`${API}/admin/telegram/link`, {}, { headers }), "¡Telegram vinculado! Revisa tu chat");
  const test = () => run("test", () => axios.post(`${API}/admin/telegram/test`, {}, { headers }), "Mensaje de prueba enviado");
  const testEmail = async () => {
    setBusy("mail");
    try {
      const res = await axios.post(`${API}/admin/email/test`, {}, { headers });
      toast.success(`Correo de prueba enviado a ${res.data.to}. Mira también la carpeta de spam`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el correo de prueba");
    } finally {
      setBusy("");
    }
  };
  const disconnect = () => run("off", () => axios.put(`${API}/admin/telegram`, { token: "" }, { headers }), "Telegram desconectado");
  const savePrefs = (patch) =>
    run("prefs", () => axios.put(`${API}/admin/telegram`, patch, { headers }), "Preferencias guardadas");

  if (!cfg) return <div className="mt-6 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  const inputCls =
    "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold";
  const btnCls =
    "inline-flex w-fit items-center gap-2 rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium px-6 py-3 transition-colors duration-200";
  const linked = Boolean(cfg.chat_id);

  return (
    <section data-testid="admin-telegram-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Avisos por Telegram</h1>
      <p className="text-sm text-taupe mt-1">
        Recibe en tu Telegram cada nueva cita, los cambios de estado y tu agenda de cada mañana.
      </p>

      <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-4">
        <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Correos a tus clientas</p>
        <p className="text-sm text-cocoa">
          Tus clientas reciben un correo cuando piden la cita («solicitud recibida»), otro cuando tú la confirmas,
          un recordatorio el día antes y, al día siguiente de venir, uno pidiéndoles reseña.
          Si alguna te dice que no le llega, pídele que mire la carpeta de <b>Correo no deseado / Spam</b> y que marque el mensaje como «No es spam».
        </p>
        <button onClick={testEmail} disabled={busy === "mail"} data-testid="email-test-btn" className={`${btnCls} w-fit`}>
          {busy === "mail" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar correo de prueba
        </button>
      </div>

      <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-8">
        <div data-testid="telegram-status" className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${linked ? "bg-emerald-500" : cfg.has_token ? "bg-amber-400" : "bg-sand"}`} />
          <span className="text-sm text-cocoa">
            {linked
              ? `Conectado con ${cfg.chat_name || "tu chat"} vía @${cfg.bot_username}`
              : cfg.has_token
              ? `Bot @${cfg.bot_username} guardado · falta vincular tu chat`
              : "Sin conectar"}
          </span>
        </div>

        {!cfg.has_token && (
          <div className="grid gap-3">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Paso 1 · Crea tu bot</p>
            <ol className="text-sm text-cocoa list-decimal pl-5 space-y-1">
              <li>Abre Telegram y busca <b>@BotFather</b>.</li>
              <li>Escribe <code className="bg-cream px-1 rounded">/newbot</code>, ponle un nombre (ej. <i>ISI'S Nails Avisos</i>) y un usuario que termine en <i>bot</i>.</li>
              <li>Copia el <b>token</b> que te da (parecido a <i>123456:ABC-DEF…</i>) y pégalo aquí.</li>
            </ol>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Token de @BotFather"
                data-testid="telegram-token-input"
                className={inputCls}
              />
              <button onClick={saveToken} disabled={!token.trim() || busy === "token"} data-testid="telegram-save-token-btn" className={btnCls}>
                {busy === "token" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Guardar bot
              </button>
            </div>
          </div>
        )}

        {cfg.has_token && !linked && (
          <div className="grid gap-3">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Paso 2 · Vincula tu chat</p>
            <p className="text-sm text-cocoa">
              Abre{" "}
              <a href={`https://t.me/${cfg.bot_username}`} target="_blank" rel="noreferrer" data-testid="telegram-bot-link" className="text-gold underline">
                @{cfg.bot_username}
              </a>{" "}
              en Telegram, pulsa <b>Iniciar</b> (o escribe /start) y después pulsa el botón de abajo.
            </p>
            <button onClick={link} disabled={busy === "link"} data-testid="telegram-link-btn" className={btnCls}>
              {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Ya le he escrito, vincular
            </button>
          </div>
        )}

        {linked && (
          <>
            <div className="grid gap-3">
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Agenda de cada mañana</p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => savePrefs({ daily_summary: !cfg.daily_summary })}
                  data-testid="telegram-daily-toggle"
                  className={`rounded-full text-sm font-medium px-4 py-2 border transition-colors duration-200 ${
                    cfg.daily_summary ? "bg-gold text-white border-gold" : "bg-cream text-taupe border-sand"
                  }`}
                >
                  {cfg.daily_summary ? "Activada" : "Desactivada"}
                </button>
                <span className="text-sm text-cocoa">a las</span>
                <select
                  value={cfg.summary_hour}
                  onChange={(e) => savePrefs({ summary_hour: Number(e.target.value) })}
                  data-testid="telegram-summary-hour"
                  className="rounded-xl border border-sand bg-cream px-3 py-2 text-sm text-obsidian"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={test} disabled={busy === "test"} data-testid="telegram-test-btn" className={btnCls}>
                {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar mensaje de prueba
              </button>
              <button
                onClick={disconnect}
                disabled={busy === "off"}
                data-testid="telegram-disconnect-btn"
                className="inline-flex items-center gap-2 rounded-xl border border-sand text-taupe hover:text-obsidian text-sm px-6 py-3 transition-colors duration-200"
              >
                <Unplug className="w-4 h-4" /> Desconectar
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
